/**
 * Wire shapes of a task (`tareas/{project}/search` and `tareas/{project}/store-update`), as the
 * production modal in `pages/tema` reads them. Keys are the API's own.
 */

export interface TaskConstraints {
  max_quantity_upload_files: number;
  /** In megabytes. */
  max_size_upload_files: number;
}

/**
 * A resource of a task, in either direction:
 * - `project_resources`: the reference files the teacher attached (`title`, `file`, `resource_id`).
 * - `user_resources`: what the learner has in the task. `type_resource: 'file'` is a file picked in
 *   this session and still to be sent (`upload` holds the `File`); `'media'` is one already stored.
 *   The extra keys of a stored media travel back untouched, the way production appends them.
 */
export interface TaskResource {
  resource_id?: number;
  title?: string;
  name?: string;
  /** Path of a reference file, relative to the bucket (or an absolute https url). */
  file?: string;
  size?: number;
  type_resource?: 'file' | 'media';
  /** Only for a file picked in this session: the `File` to send. */
  upload?: File;
  [key: string]: unknown;
}

export interface TaskData {
  indications: string | null;
  project_resources: TaskResource[];
  user_resources: TaskResource[];
  /** Feedback from whoever reviewed the task. Shown only when present. */
  msg_to_user: string | null;
  constraints: TaskConstraints | null;
}

/** What `tareas/{project}/store-update` answers. `type === 'success'` is the only success. */
export interface TaskSubmitResponse {
  type?: string;
  data?: {
    msg?: string;
    status_label?: string;
    status_code?: string | number;
  };
}

/** Emitted by `app-task-delivery` once the task is sent — the same three values production keeps. */
export interface TaskSavedEvent {
  msg: string;
  status_label: string | null;
  status_code: string | number | null;
}

/** MIME types production accepts for a task file (`tema.page.ts`'s `validateFileType`). */
export const TASK_ACCEPTED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/x-eps',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/ogg',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

/** Same `accept` list the production file input carries. */
export const TASK_FILE_ACCEPT =
  'image/jpeg,image/gif,image/png,application/pdf,image/x-eps, .xls,.xlsx, .doc, .docx, .ppt, .pptx, .pptm, .ppsm, .ppsx, video/*';
